/**
 * Documentation Content Data
 * Contains the actual content for each documentation section
 */

export const DOCUMENTATION_CONTENT = {
  3: {
    id: "3",
    title: "Accounts & Transactions",
    description: "Upload, review, allocate, link, and remove bank transactions from Data Entry >> Accounts.",
    features: [
      "Add or select a bank account before uploading",
      "Add an account group from the Account Group column",
      "Use Link in the first column to match a transaction to an invoice",
      "Use Enable Delete before Delete All, or X for an individual row",
      "Use Load All Transactions in Filters & Search when records appear missing",
      "The + More Options menu also contains Load All Transactions and the detailed help guides",
    ],
  },
  4: {
    id: "4",
    title: "Upload CSV Bank Statements",
    description: "Upload CSV statements on Data Entry >> Accounts with Date, Description, and Amount columns.",
    features: [
      "Dates use YYYY/MM/DD",
      "Amounts must be numeric in Excel",
      "Positive values are receipts and negative values are payments",
      "PDF statements can first be converted with the extraction flow",
      "Review imported rows and totals before allocation",
    ],
  },
  9: {
    id: "9",
    title: "Invoices & Billing",
    description: "Create and manage invoices from Invoicing >> Invoices, then match payments from Accounts.",
    features: [
      "Confirm the client, line items, VAT, and invoice date before saving",
      "Send saved invoices from the invoice workflow",
      "Track paid status in the invoice summary",
      "Match a bank transaction with the Link button on Accounts",
    ],
  },
  8: {
    id: "8",
    title: "Trial Balance & Ledger",
    emoji: "⚖️",
    description:
      "View and filter your trial balance and ledger transactions with powerful date filtering capabilities.",
    features: [
      "Real-time transaction viewing",
      "Advanced date range filtering",
      "Account group filtering",
      "Search functionality",
      "CSV export",
      "Opening balance calculations",
      "Click a Trial Balance account to open its filtered ledger",
      "Use Show all dates for this account to inspect the account's complete history",
    ],
    dateFilterInfo: {
      enabled: true,
      description:
        "The Ledger now includes an enhanced DateRangePicker component allowing you to:",
      capabilities: [
        "Select custom date ranges",
        "Choose from preset periods (This Month, Last Month, This Quarter, etc.)",
        "Filter by specific business years",
        "View all-time data",
        "Quick clear filters",
      ],
    },
  },

  12: {
    id: "12",
    title: "SOCI - Statement of Comprehensive Income",
    emoji: "💹",
    description:
      "Generate professional income statements with flexible date filtering for any reporting period.",
    features: [
      "Automated income calculation",
      "Expense categorization",
      "Profit/loss analysis",
      "Multi-year comparison",
      "Date range filtering",
      "PDF export",
    ],
    dateFilterInfo: {
      enabled: true,
      description:
        "SOCI supports advanced date filtering to view income for specific periods:",
      capabilities: [
        "Custom date ranges for income analysis",
        "Period-over-period comparisons",
        "Monthly, quarterly, and annual views",
        "Quick preset selections",
        "Dynamic calculations based on selected dates",
      ],
    },
  },

  13: {
    id: "13",
    title: "SOFP - Statement of Financial Position",
    emoji: "🏦",
    description:
      "View your balance sheet at any point in time with the new date filtering feature.",
    features: [
      "Assets vs Liabilities overview",
      "Equity calculations",
      "Retained income tracking",
      "Point-in-time snapshots",
      "End-date filtering",
      "Professional formatting",
    ],
    dateFilterInfo: {
      enabled: true,
      description:
        "SOFP uses end-date filtering to show financial position at a specific point:",
      capabilities: [
        "Filter by end date to see position at any time",
        "Cumulative transaction calculations",
        "Retained earnings up to selected date",
        "Quick year-end selections",
        "Historical balance sheet views",
      ],
      note: "Note: SOFP filters by END DATE only, as it shows cumulative financial position.",
    },
  },

  14: {
    id: "14",
    title: "Cash Flow Statement",
    emoji: "💸",
    description:
      "Track cash movements with the enhanced cash flow statement and date filtering.",
    features: [
      "Operating activities",
      "Investing activities",
      "Financing activities",
      "Net cash flow calculation",
      "Date range filtering",
      "Period comparisons",
    ],
    dateFilterInfo: {
      enabled: true,
      description:
        "Cash Flow statements now support date filtering for period analysis:",
      capabilities: [
        "Custom period cash flow analysis",
        "Compare cash movements across periods",
        "Month-over-month tracking",
        "Quick preset period selections",
        "Export filtered results",
      ],
    },
  },
  15: {
    id: "15",
    title: "Financial Statement Generation",
    description: "Generate reports after validating the selected entity, year, ledger, and trial balance.",
    features: [
      "Use Financials >> Trial Balance to validate balances",
      "Click Auto Generate Financials when statements do not reflect recent changes",
      "Trace report differences from Financials >> Ledger",
      "Review statement output before exporting",
    ],
  },
  16: {
    id: "16",
    title: "Users, Permissions & Support",
    description: "Manage access from Settings >> User Management and diagnose access issues in the selected entity context.",
    features: [
      "Add users or client access in User Management",
      "Confirm the correct entity and year before troubleshooting permissions",
      "Refresh and sign in again after access changes",
      "Use Settings >> Audit logs to review activity and suggested duplicates",
      "Share the page, exact error, period, and a screenshot for account-specific support",
    ],
  },
  17: {
    id: "17",
    title: "Billing & Payments",
    description: "Manage subscription information from Settings >> Payments.",
    features: [
      "Subscriptions are not charged per company added",
      "Review the current plan and payment status before escalating a billing issue",
    ],
  },
  18: {
    id: "18",
    title: "Bank Accounts & Feeds",
    description: "Add accounts from Data Entry >> Accounts and remove an account from Data Entry >> Bank Update.",
    features: [
      "Use Add Bank Account to add multiple bank accounts",
      "Select the bank account on Bank Update before deleting it",
      "Check the selected entity and year before reviewing balances",
    ],
  },
};

// Helper function to get content by ID
export const getDocumentationContent = (id) => {
  return DOCUMENTATION_CONTENT[id] || null;
};

// Get all items with date filter features
export const getDateFilterEnabledContent = () => {
  return Object.values(DOCUMENTATION_CONTENT).filter(
    (content) => content.dateFilterInfo?.enabled
  );
};
